import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clinicApi } from '../services/clinicApi';
import { getApiErrorMessage } from '@shared/utils/apiError';
import { ClinicStatus } from '../types/clinic';
import type { ClinicDetailDto } from '../types/clinic';

interface UseClinicDataResult {
  clinic: ClinicDetailDto | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  /** Whether the clinic is in a verifiable status (Active or PendingVerification). */
  canVerify: boolean;
  /** Fires the verify mutation; resolves when done. */
  verify: () => void;
  verifyPending: boolean;
  verifyError: string | null;
  clearVerifyError: () => void;
  /** Fires the clinic status update mutation (e.g., Active/Inactive). */
  setStatus: (status: ClinicStatus) => void;
  statusPending: boolean;
  statusError: string | null;
  clearStatusError: () => void;
}

interface UseClinicDataOptions {
  /** Called after a successful verify mutation (e.g., to close a modal). */
  onVerifySuccess?: () => void;
  /** Called after a successful status change (e.g., to close a modal). */
  onStatusSuccess?: () => void;
}

/**
 * Encapsulates fetching a single clinic by ID, plus the "verify clinic" mutation.
 * Keeps ClinicDetailPage free of data-fetching boilerplate.
 */
export function useClinicData(clinicId: number, options?: UseClinicDataOptions): UseClinicDataResult {
  const queryClient = useQueryClient();
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const { data: clinic, isLoading, error } = useQuery({
    queryKey: ['clinic', clinicId],
    queryFn: () => clinicApi.getClinicById(clinicId),
    enabled: !Number.isNaN(clinicId),
  });

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['clinic', clinicId] });
  };

  const verifyMutation = useMutation({
    mutationFn: () => clinicApi.verifyClinic(clinicId),
    onSuccess: () => {
      setVerifyError(null);
      queryClient.invalidateQueries({ queryKey: ['clinic', clinicId] });
      options?.onVerifySuccess?.();
    },
    onError: (err: unknown) => {
      setVerifyError(getApiErrorMessage(err, 'Failed to verify clinic'));
    },
  });

  const statusMutation = useMutation({
    mutationFn: (newStatus: ClinicStatus) => clinicApi.updateStatus(clinicId, newStatus),
    onSuccess: () => {
      setStatusError(null);
      queryClient.invalidateQueries({ queryKey: ['clinic', clinicId] });
      options?.onStatusSuccess?.();
    },
    onError: (err: unknown) => {
      setStatusError(getApiErrorMessage(err, 'Failed to update clinic status'));
    },
  });

  const canVerify = !!clinic && (
    clinic.status === ClinicStatus.Active ||
    clinic.status === ClinicStatus.PendingVerification
  );

  return {
    clinic,
    isLoading,
    error: error as Error | null,
    refetch,
    canVerify,
    verify: () => verifyMutation.mutate(),
    verifyPending: verifyMutation.isPending,
    verifyError,
    clearVerifyError: () => setVerifyError(null),
    setStatus: (status: ClinicStatus) => statusMutation.mutate(status),
    statusPending: statusMutation.isPending,
    statusError,
    clearStatusError: () => setStatusError(null),
  };
}
